import React from "react"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';

import AssignCollectionAccordion from "./AssignCollectionAccordion"
import CollapsedCollection from "./CollapsedCollection"
import EditSampleSmallCards from "./EditSampleSmallCards"
import MassEditPane from "./MassEditPane"
import LoadMoreButton from "./LoadMoreButton"
import SFSnackbar from "./SFSnackbar"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material"
import { StyledEngineProvider } from "@mui/material/styles"

import { observer, Provider } from "mobx-react"
import sampleStore from "../stores/sampleStore"
import collectionStore from "../stores/collectionStore"
import userStore from "../stores/userStore"
import snackbarStore from "../stores/snackbarStore"
import Sample from "../models/sample"
import Collection from "../models/collection"
import User from "../models/user"
import { logEvent } from "../utils/analytics"

const BatchUpdateContainer = styled('div')`
  display: flex;
  top: 65px;
  bottom: 0;
  left: 0;
  right: 0;
`

const RightContainer = styled('div')`
  position: relative;
  top: 0;
  bottom: 0;
  right: 0;
  overflow: scroll;
  flex-grow: 2;
`

const LeftContainer = styled('div')`
  width: 570px;
  min-width: 570px;
  position: relative;
  left: 0;
  top: 0;
  right: 570px;
  bottom: 0;
  border-left: 1px solid #e0e0e0;
  padding: 5px 20px;
  background-color: whitesmoke;
  z-index: 100;
`

const SubmitRowContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
  padding: 16px 38px;
  background-color: white;

  &.fixed {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 5;
  }
`

const SubmitButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const stores = {
  sampleStore,
  collectionStore,
  userStore,
  snackbarStore,
}

@observer
class BatchUpdateV2 extends React.Component {
  static defaultProps = {
    infiniteScrollEnabled: true,
  }

  constructor(props) {
    super(props)
    this.state = {
      allSamplesValid: false,
      allSamplesSaved: false,
      successModalOpen: false,
      isSubmitting: false,
      showError: false,
      hasSubmittedOnce: false,
    }
    sampleStore.showSequencer = false
    props.samples.forEach((sampleJSON) => {
      sampleStore.addSample(new Sample(sampleJSON))
    })
    if (props.mode == "review") {
      sampleStore.onlySaveSelected = true
    }
    collectionStore.availableCollections = props.availableCollections.map(
      (collectionData) => new Collection(collectionData)
    )

    if (props.collection) {
      collectionStore.focusedCollection = new Collection(props.collection)
    } else {
      collectionStore.focusedCollection = new Collection({})
    }
    collectionStore.focusedCollection.samples = sampleStore.samples
    userStore.currentUser = new User(props.currentUser)
  }

  componentDidMount() {
    if (sampleStore.samples.length === 1) {
      sampleStore.selectSample(sampleStore.samples[0])

      const canModerate = userStore.currentUser.canModerate
      if (canModerate) {
        sampleStore.getSuggestedNames({ setNameToSuggested: false })
      }
    }
  }

  handleSubmit = async (samples) => {
    const { csrfToken, mode } = this.props
    this.setState({ isSubmitting: true, hasSubmittedOnce: true })

    const sampleValidResults = []

    samples.forEach((sample) => {
      sample.touchAllFields()
      const isSampleValid = _.isEmpty(sample.validationErrors)
      sampleValidResults.push(isSampleValid)
    })

    if (sampleValidResults.includes(false)) {
      this.setState({
        allSamplesValid: false,
        isSubmitting: false,
        showError: true,
      })
      return
    } else {
      this.setState({ allSamplesValid: true, showError: false })
    }

    const response = await sampleStore.saveSamples(samples, csrfToken)

    if (response.status === 200) {
      if (mode === "edit_uploaded") {
        logEvent("EditUploadedV2 Success", {
          sampleCount: samples.length,
          averageTagCount: sampleStore.averageTagCount,
        })
      }
      this.setState({
        allSamplesSaved: true,
        isSubmitting: false,
      })

      if (mode === "review") {
        sampleStore.removePublishedSamples()
      } else {
        this.setState({ successModalOpen: true })
      }

      if (mode === "edit_sample") {
        const sampleUrls = response.data.sampleUrls
        const sampleRedirectUrl = sampleUrls[0] + "?updated=true"
        window.location.href = sampleRedirectUrl;
      }
    } else {
      // Something went wrong
      const errorMessages =  response.response.data.errors
      snackbarStore.showSnackbar(`Something went wrong: ${ errorMessages.join(", ")}.`, "error")

      this.setState({ allSamplesSaved: false, isSubmitting: false })
      return
    }
  }

  handleMoreUploadClick() {
    const baseURL = window.location.origin
    const redirectURL = baseURL + "/samples/new"
    window.location.href = redirectURL;
  }

  handleGoToHomepageClick() {
    const baseURL = window.location.origin
    window.location.href = baseURL;
  }

  handleClose = () => {
    this.setState({ successModalOpen: false })
  }

  renderUploadSucessModal() {
    const { successModalOpen } = this.state

    return (
      <Dialog open={successModalOpen}>
        <DialogTitle>Thank you for uploading!</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your samples have been submitted for review.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleMoreUploadClick} color="primary">
            Upload more samples
          </Button>
          <Button
            onClick={this.handleGoToHomepageClick}
            color="primary"
            autoFocus
          >
            Go to homepage
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderCollectionUpdateSucess() {
    const { successModalOpen } = this.state

    return (
      <Dialog onClose={this.handleClose} open={successModalOpen}>
        <DialogTitle>Collection updated succesfully</DialogTitle>

        <DialogActions>
          <Button
            onClick={this.handleClose}
            color="primary"
            autoFocus
            size="large"
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  renderLoadMore() {
    const { pagyMetadata, infiniteScrollEnabled } = this.props

    if (pagyMetadata) {
      return (
        <>
          <LoadMoreButton
            pagyMetadata={pagyMetadata}
            infiniteScrollEnabled={infiniteScrollEnabled}
          />
          <div
            css={css`
              height: 50px;
            `}
          />
        </>
      )
    }
  }

  renderErrorText() {
    const { showError } = this.state

    if (showError && sampleStore.anyValidationErrors) {
      return (
        <p
          css={css`
            color: red;
            margin: 0 0 5px 0;
          `}
        >
          Please fix the above errors
        </p>
      )
    } else {
      return null
    }
  }

  renderCollectionArea() {
    const { mode } = this.props

    const collection = collectionStore.focusedCollection

    if (mode === "review") {
      return null
    }
    if (collection.isPersisted) {
      return <CollapsedCollection />
    } else {
      return <AssignCollectionAccordion />
    }
  }

  render() {
    let { categories, tagOptions, mode } = this.props
    const { isSubmitting, hasSubmittedOnce } = this.state
    const samples = sampleStore.samples
    const selectedSamples = sampleStore.selectedSamples
    const samplesToSubmit = mode == "review" ? selectedSamples : samples
    const submitButtonText = mode == "review" ? "Submit Selected" : "Submit"

    // StylesProvider is needed to override specificity of built-in MUi styles
    // https://material-ui.com/styles/advanced/#css-injection-order
    return (
      <Provider {...stores}>
        <StyledEngineProvider injectFirst>
          <BatchUpdateContainer>
            <div className="divider"></div>
            <LeftContainer>
              <MassEditPane
                categories={categories}
                tagOptions={tagOptions}
                samples={samples}
                mode={mode}
              />
            </LeftContainer>
            <RightContainer>
              {this.renderCollectionArea()}
              <EditSampleSmallCards samples={samples} categories={categories} />
              {mode == "review" && this.renderLoadMore()}
              <SubmitRowContainer className={mode == "review" ? "fixed" : ""}>
                <SubmitButtonContainer>
                  {this.renderErrorText()}
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    size="large"
                    onClick={() => this.handleSubmit(samplesToSubmit)}
                    disabled={
                      isSubmitting ||
                      samplesToSubmit.length == 0 ||
                      (hasSubmittedOnce && sampleStore.anyValidationErrors)
                    }
                  >
                    {isSubmitting ? "Submitting" : submitButtonText}
                    {isSubmitting && (
                      <CircularProgress
                        css={css`
                          margin-left: 8px;
                        `}
                        size={24}
                      />
                    )}
                  </Button>
                </SubmitButtonContainer>
              </SubmitRowContainer>
            </RightContainer>
            {mode == "edit_uploaded" && this.renderUploadSucessModal()}
            {mode == "edit_collection" && this.renderCollectionUpdateSucess()}
            <SFSnackbar />
          </BatchUpdateContainer>
        </StyledEngineProvider>
      </Provider>
    )
  }
}

export default BatchUpdateV2
